/**
 * Planning — demand forecasts and the MRP runs that turn them into supply
 * recommendations.
 *
 * MRP is the one screen here that starts real work: a run reads the forecast,
 * on-hand stock and open orders, and proposes what to buy or make. It is
 * scoped by a horizon, so the horizon is a deliberate input rather than a
 * hidden default.
 */
import { Link } from "react-router-dom";
import { ResourceListPage } from "@/components/ResourceListPage";
import {
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  Skeleton,
  StatCard,
  StatusBadge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { formatDateTime, formatQuantity } from "@/lib/money";

export interface ForecastRow {
  id: string;
  product_id: string;
  period: string;
  quantity: string;
  confidence_percent: number | null;
  status: string;
  created_at: string;
}

export interface MrpRunRow {
  id: string;
  run_number: string;
  status: string;
  horizon_days: number;
  recommendations: unknown[] | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

export function ForecastsPage() {
  return (
    <ResourceListPage<ForecastRow>
      title="Demand forecasts"
      subtitle="Expected demand per product per period. MRP plans supply against these numbers."
      breadcrumb={["Planning", "Forecasts"]}
      path="/planning/forecasts"
      queryKey={["planning", "forecasts"]}
      createLabel="New forecast"
      emptyTitle="No forecasts"
      emptyMessage="A forecast is expected demand for one product in one period. With none, MRP can only plan against firm orders."
      createFields={[
        {
          field: "productId",
          label: "Product ID",
          required: true,
          hint: "The product this forecast covers.",
        },
        {
          field: "period",
          label: "Period",
          required: true,
          placeholder: "2026-04",
          hint: "The period demand is expected in.",
        },
        {
          field: "quantity",
          label: "Quantity",
          required: true,
          type: "number",
        },
        {
          field: "confidencePercent",
          label: "Confidence %",
          type: "number",
          hint: "How much weight to give this number. Defaults to 50.",
        },
      ]}
      toBody={(form) => ({
        productId: form.productId,
        period: form.period,
        // The API type-checks quantity as a string and confidence as a number.
        quantity: String(form.quantity),
        confidencePercent: form.confidencePercent
          ? Number(form.confidencePercent)
          : 50,
      })}
      columns={[
        {
          key: "product_id",
          label: "Product",
          render: (r) => (
            <span className="atlas-mono">{r.product_id.slice(0, 8)}</span>
          ),
        },
        {
          key: "period",
          label: "Period",
          render: (r) => <span className="atlas-mono">{r.period}</span>,
        },
        {
          key: "quantity",
          label: "Forecast qty",
          numeric: true,
          render: (r) => formatQuantity(r.quantity),
        },
        {
          key: "confidence_percent",
          label: "Confidence",
          numeric: true,
          render: (r) =>
            r.confidence_percent === null ? "—" : `${r.confidence_percent}%`,
        },
        {
          key: "status",
          label: "Status",
          render: (r) => <StatusBadge status={r.status} />,
        },
      ]}
    />
  );
}

export function PlanningMrpPage() {
  return (
    <ResourceListPage<MrpRunRow>
      title="MRP runs"
      subtitle="Each run reads forecasts, stock and open orders, then proposes what to buy or make."
      breadcrumb={["Planning", "MRP"]}
      path="/planning/mrp/runs"
      createPath="/planning/mrp/run"
      // NUM-P0: the run is numbered by the server and the route takes an
      // Idempotency-Key, one per dialog.
      idempotent
      queryKey={["planning", "mrp-runs"]}
      createLabel="Run MRP"
      emptyTitle="No MRP runs"
      emptyMessage="An MRP run is a planning calculation, not a commitment — it produces recommendations you then choose to act on. Nothing is ordered automatically."
      createFields={[
        {
          field: "horizonDays",
          label: "Planning horizon (days)",
          required: true,
          type: "number",
          initial: "30",
          hint: "How far ahead to plan. A longer horizon catches long-lead items; a shorter one is more accurate.",
        },
      ]}
      toBody={(form) => ({ horizonDays: Number(form.horizonDays || 30) })}
      columns={[
        {
          key: "run_number",
          label: "Run",
          render: (r) => <span className="atlas-mono">{r.run_number}</span>,
        },
        {
          key: "status",
          label: "Status",
          render: (r) => <StatusBadge status={r.status} />,
        },
        {
          key: "horizon_days",
          label: "Horizon",
          numeric: true,
          render: (r) => `${r.horizon_days} days`,
        },
        {
          key: "recommendations",
          label: "Recommendations",
          numeric: true,
          render: (r) =>
            Array.isArray(r.recommendations) ? r.recommendations.length : 0,
        },
        {
          key: "started_at",
          label: "Started",
          render: (r) => (r.started_at ? formatDateTime(r.started_at) : "—"),
        },
        {
          key: "finished_at",
          label: "Finished",
          // A run that started but never finished is stuck, and that reads
          // very differently from one that has not begun.
          render: (r) =>
            r.finished_at ? (
              formatDateTime(r.finished_at)
            ) : r.started_at ? (
              <span style={{ color: "var(--atlas-warning)" }}>
                Still running
              </span>
            ) : (
              "—"
            ),
        },
      ]}
    />
  );
}

export function PlanningPage() {
  const forecasts = useApiList<ForecastRow>(
    ["planning", "forecasts"],
    "/planning/forecasts",
  );
  const runs = useApiList<MrpRunRow>(
    ["planning", "mrp-runs"],
    "/planning/mrp/runs",
  );

  if (forecasts.isLoading || runs.isLoading) return <Skeleton rows={8} />;

  const runRows = runs.data?.items ?? [];
  const latest = runRows[0];
  const recommendations = Array.isArray(latest?.recommendations)
    ? latest.recommendations.length
    : 0;

  return (
    <div>
      <PageHeader
        title="Planning"
        subtitle="Demand forecasts and the supply plan derived from them."
      />

      <div className="atlas-grid atlas-grid--3 mb-4">
        <StatCard label="Forecasts" value={forecasts.data?.total ?? 0} />
        <StatCard label="MRP runs" value={runs.data?.total ?? 0} />
        <StatCard
          label="Open recommendations"
          value={recommendations}
          meta={latest ? `From run ${latest.run_number}` : "No run yet"}
        />
      </div>

      {(forecasts.data?.total ?? 0) === 0 && runRows.length === 0 ? (
        <EmptyState
          title="Nothing planned yet"
          message="Start with a demand forecast, then run MRP against it. Without forecasts, MRP can only plan against orders you already hold."
          action={
            <Link to="/app/planning/forecasts">
              <span className="atlas-btn atlas-btn--primary">
                Add a forecast
              </span>
            </Link>
          }
        />
      ) : (
        <Card title="Recent MRP runs">
          {runRows.length === 0 ? (
            <EmptyState
              title="No MRP run yet"
              message="Forecasts are in place. Run MRP to turn them into buy and make recommendations."
              action={
                <Link to="/app/planning/mrp">
                  <span className="atlas-btn atlas-btn--primary">Run MRP</span>
                </Link>
              }
            />
          ) : (
            <DataTable<MrpRunRow>
              rows={runRows.slice(0, 8)}
              columns={[
                {
                  key: "run_number",
                  label: "Run",
                  render: (r) => (
                    <span className="atlas-mono">{r.run_number}</span>
                  ),
                },
                {
                  key: "status",
                  label: "Status",
                  render: (r) => <StatusBadge status={r.status} />,
                },
                {
                  key: "horizon_days",
                  label: "Horizon",
                  numeric: true,
                  render: (r) => `${r.horizon_days}d`,
                },
                {
                  key: "recommendations",
                  label: "Recommendations",
                  numeric: true,
                  render: (r) =>
                    Array.isArray(r.recommendations)
                      ? r.recommendations.length
                      : 0,
                },
              ]}
            />
          )}
        </Card>
      )}
    </div>
  );
}
